import apiRoutes from "@/src/apiRoutes";
import { Button } from "@/src/components/Button/Button";
import InputComponent from "@/src/components/Input/Input";
import { Modal } from "@/src/components/Modal/Modal";
import Passwordinput from "@/src/components/PasswordInput/PasswordInput";
import { textVariants } from "@/src/components/Text/Text";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/src/components/shad/card";
import useUrlState from "@/src/hooks/use-url-state";
import api from "@/src/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

const formState = z
  .object({
    number: z
      .string()
      .length(10, { message: "Number should be exactly 10 digits" })
      .refine((num) => /^\d+$/.test(num), {
        message: "Please enter a valid number",
      }),
    ussd_pin: z
      .string()
      .length(4, { message: "Pin should be exactly 4 digits" })
      .refine((num) => /^\d+$/.test(num), {
        message: "Please enter a valid number",
      }),
    verification_code: z
      .string()
      .length(6, { message: "Verification code should be exactly 6 digits" })
      .optional(),
    verify_pin: z.string({ invalid_type_error: "Enter a number" }),
  })
  .refine((data) => data.ussd_pin === data.verify_pin, {
    message: "The pins entered do not match",
    path: ["verify_pin"],
  });

const Activate = () => {
  type formType = z.infer<typeof formState>;
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
  } = useForm<formType>({
    resolver: zodResolver(formState),
  });
  const [modal, setModal] = useUrlState("modal");
  const queryClient = useQueryClient();

  const addUssd = async (data: formType) => {
    const newData = _.omit(data, "verify_pin");
    const res = await api.post(apiRoutes.ussdSettings, {
      type: "add",
      kind: "activate",
      ...newData,
    });
    return res.data;
  };

  const { mutate, isLoading } = useMutation({
    mutationKey: ["add_ussd"],
    mutationFn: addUssd,
    onSuccess: (data) => {
      if (data?.success) {
        setModal(undefined);
        queryClient.invalidateQueries({ queryKey: ["check_enablers"] });
        toast.success(data?.info);
      } else {
        toast.warning(data?.info);
      }
    },
  });

  const sendUssdOtp = async () => {
    const res = await api.get(apiRoutes.ussdSettings);
    return res.data;
  };

  const { mutate: sendOtp, isLoading: otpLoad } = useMutation({
    mutationFn: sendUssdOtp,
    mutationKey: ["send_ussd_otp"],
    onSuccess: (data) => {
      if (data?.success) {
        setModal("open");
        toast.success(data?.info);
      } else {
        toast.warning(data?.info);
      }
    },
  });

  const submit: SubmitHandler<formType> = async () => {
    sendOtp();
  };

  return (
    <div>
      <Card>
        <CardHeader>
          <CardTitle>Activate USSD service</CardTitle>
          <CardDescription>Your phone number and pin code</CardDescription>
        </CardHeader>
        <form autoComplete="off" onSubmit={handleSubmit(submit)}>
          <CardContent>
            <div className="grid gap-3">
              <InputComponent
                errors={errors.number?.message}
                {...register("number", { required: true })}
                label="Enter the phone number you would like to use"
                type={"number"}
              />
              <Passwordinput
                autoComplete="off"
                errors={errors.ussd_pin?.message}
                {...register("ussd_pin", { required: true })}
                label="Enter a four digit pin you would like to use to verify transactions"
                type={"number"}
              />
              <Passwordinput
                autoComplete="off"
                errors={errors.verify_pin?.message}
                {...register("verify_pin", { required: true })}
                label="Verify the pin entered"
                type={"number"}
              />
            </div>
          </CardContent>
          <CardHeader className=" bg-back p-2 flex flex-row justify-between items-center border-t rounded-b-lg">
            <span className={textVariants({ variant: "desc", font: "medium" })}>
              Add number to USSD service
            </span>

            <Button loading={otpLoad} type="submit" variant="primary">
              Add Number
            </Button>
          </CardHeader>
        </form>
      </Card>
      <Modal
        open={modal === "open"}
        setOpen={(val) => setModal(val ? undefined : "open")}
        title="Verify code sent to email"
        description="A verification code has been sent to your email. Kindly check and verify."
        footer={
          <div>
            <Button
              loading={isLoading}
              onClick={() => mutate(getValues())}
              variant={"outline"}
            >
              Submit
            </Button>
          </div>
        }
        size={"sm"}
      >
        <div>
          <InputComponent
            onChange={(e) => setValue("verification_code", e.target.value)}
            label="Enter otp sent to your email"
            type="number"
          />
        </div>
      </Modal>
    </div>
  );
};

export default Activate;
